import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Heart, ShoppingCart, Star, Gift } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useCart } from '../context/CartContext';
import { useWishlist } from '../context/WishlistContext';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { PLACEHOLDER_IMAGE } from '../lib/placeholder';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

export function ProductCard({ product }) {
  const getImageUrl = (url) => {
    if (!url) return PLACEHOLDER_IMAGE;
    if (url.startsWith('http')) return url;
    return `${BACKEND_URL}${url}`;
  };

  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { addToCart } = useCart();
  const { addToWishlist, removeFromWishlist, isInWishlist } = useWishlist();

  const inWishlist = isInWishlist(product.product_id);
  const discount = product.original_price
    ? Math.round((1 - product.price / product.original_price) * 100)
    : 0;

  const handleAddToCart = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await addToCart(product.product_id, 1);
      toast.success('Added to cart');
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleWishlist = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) {
      toast.error('Please login to add to wishlist');
      navigate('/login', { state: { from: location.pathname } });
      return;
    }
    try {
      if (inWishlist) {
        await removeFromWishlist(product.product_id);
        toast.success('Removed from wishlist');
      } else {
        await addToWishlist(product.product_id);
        toast.success('Added to wishlist');
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(price);
  };

  return (
    <Link
      to={`/product/${product.product_id}`}
      className="group block product-card bg-white rounded-3xl overflow-hidden min-w-0"
      data-testid={`product-card-${product.product_id}`}
    >
      <div className="relative aspect-square bg-white border-b border-gray-50 flex items-center justify-center overflow-hidden">
        <img
          src={getImageUrl(product.images?.[0])}
          alt={product.name}
          onError={(e) => { if (e.currentTarget.src !== PLACEHOLDER_IMAGE) e.currentTarget.src = PLACEHOLDER_IMAGE; }}
          className="max-w-full max-h-full object-contain p-4 group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
        />

        {/* Badges */}
        <div className="absolute top-3 left-3 flex flex-col gap-2">
          {discount > 0 && product.discount_type !== 'b1g1' && (
            <span className="bg-destructive text-white text-xs font-medium px-2 py-1 rounded-full">
              {product.discount_type === 'flat' ? `-₹${product.original_price - product.price}` : `-${discount}%`}
            </span>
          )}
          {product.is_bestseller && (
            <span className="bg-success text-white text-xs font-medium px-2 py-1 rounded-full">
              Bestseller
            </span>
          )}
          {product.is_trending && (
            <span className="bg-primary text-white text-xs font-medium px-2 py-1 rounded-full">
              Trending
            </span>
          )}
          {product.discount_type === 'b1g1' && (
            <span className="bg-indigo-600 text-white text-[10px] font-bold px-2 py-1 rounded-full flex items-center gap-1">
              <Gift className="w-3 h-3" />
              BUY 1 GET 1 FREE
            </span>
          )}
        </div>

        {/* Wishlist Button */}
        <button
          onClick={handleWishlist}
          className={`absolute top-3 right-3 p-2 rounded-full transition-all ${inWishlist
            ? 'bg-primary text-white'
            : 'bg-white/80 backdrop-blur text-secondary hover:bg-white'
            }`}
          data-testid={`wishlist-btn-${product.product_id}`}
        >
          <Heart className={`w-4 h-4 ${inWishlist ? 'fill-current' : ''}`} />
        </button>

        {/* Quick Add Button */}
        <div className="absolute bottom-3 left-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <Button
            onClick={handleAddToCart}
            className="w-full bg-secondary hover:bg-secondary/90 text-white rounded-full font-body text-sm"
            data-testid={`add-cart-btn-${product.product_id}`}
          >
            <ShoppingCart className="w-4 h-4 mr-2" />
            Add to Cart
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1 font-body">
          {product.brand}
        </p>
        <h3 className="font-body font-medium text-secondary mb-2 line-clamp-2 group-hover:text-primary transition-colors">
          {product.name}
        </h3>

        {/* Rating */}
        {!!product.review_count && product.review_count > 0 && (
          <div className="flex items-center gap-1 mb-2">
            <Star className="w-4 h-4 fill-warning text-warning" />
            <span className="text-sm font-medium text-secondary">{product.average_rating}</span>
            <span className="text-sm text-muted-foreground">({product.review_count})</span>
          </div>
        )}

        {/* Price */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-body font-semibold text-secondary text-lg">
            {formatPrice(product.discount_type === 'b1g1' && parseFloat(product.price) === 0 ? product.original_price : product.price)}
          </span>
          {product.original_price > product.price && product.discount_type !== 'b1g1' && (
            <>
              <span className="text-sm text-muted-foreground line-through">
                {formatPrice(product.original_price)}
              </span>
              <span className="text-[10px] font-bold text-destructive bg-destructive/5 px-2 py-0.5 rounded-full uppercase tracking-tighter">
                {product.discount_type === 'flat' ? `₹${product.original_price - product.price} OFF` : `${discount}% OFF`}
              </span>
            </>
          )}
        </div>
      </div>
    </Link>
  );
}
